import {Component, OnInit, OnDestroy} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import {SharedModule} from '../../../shared.module';
import {MatIconModule} from '@angular/material/icon';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatCardModule} from '@angular/material/card';
import {MatSelectModule} from '@angular/material/select';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-registration',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatIconModule,
    MatToolbarModule,

    CommonModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatSidenavModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatCheckboxModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './registration.html',
  styleUrl: './registration.scss'
})
export class RegistrationComponent implements OnInit, OnDestroy {
  registrationForm: FormGroup;
  loading = false;
  mobileMenuOpen = false;
  hidePassword = true;
  hideConfirmPassword = true;
  currentStep = 1;

  usernameAvailable: boolean | null = null;
  checkingUsername = false;
  private usernameSubscription: Subscription;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
  ) {
    this.registrationForm = this.fb.group({
      // User details
      username: ['', [Validators.required, Validators.minLength(3)]],
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
      // Shop details — shop code is no longer collected here, it's
      // auto-generated server-side from shopName (see RegisterAdmin.mutate
      // in the backend).
      shopName: ['', [Validators.required, Validators.minLength(2)]],
      shopAddress: ['', [Validators.required]],
      shopPhone: ['', [Validators.required, Validators.pattern('^\\+?\\d{10,13}$')]],
      shopEmail: ['', [Validators.required, Validators.email]],
      // Optional — another shop's code, shared as a link (?ref=CODE, see
      // ngOnInit) or typed in manually. Unknown codes are silently ignored
      // server-side, so no validator here.
      referralCode: [''],
      agreeToTerms: [false, Validators.requiredTrue],
    });

    this.usernameSubscription = this.registrationForm.get('username')!.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      filter(value => value && value.length >= 3),
    ).subscribe(async (username) => {
      this.checkingUsername = true;
      this.usernameAvailable = null;
      try {
        this.usernameAvailable = await this.auth.checkUsernameAvailability(username);
      } catch {
        this.usernameAvailable = null;
      } finally {
        this.checkingUsername = false;
      }
    });
  }

  ngOnInit(): void {
    // A shared referral link (e.g. .../registration?ref=CODE) pre-fills
    // the field; the visitor can still edit or clear it before submitting.
    const ref = this.route.snapshot.queryParamMap.get('ref');
    if (ref) {
      this.registrationForm.patchValue({ referralCode: ref });
    }
  }

  ngOnDestroy() {
    this.usernameSubscription?.unsubscribe();
  }

  get isStep1Valid(): boolean {
    const fields = ['username', 'firstName', 'lastName', 'email', 'password', 'confirmPassword'];
    return fields.every(f => this.registrationForm.get(f)?.valid) && this.passwordsMatch;
  }

  get passwordsMatch(): boolean {
    return this.registrationForm.get('password')?.value === this.registrationForm.get('confirmPassword')?.value;
  }

  nextStep() {
    if (this.isStep1Valid) {
      this.currentStep = 2;
    } else {
      const fields = ['username', 'firstName', 'lastName', 'email', 'password', 'confirmPassword'];
      fields.forEach(field => {
        this.registrationForm.get(field)?.markAsTouched({ onlySelf: true });
      });
    }
  }

  previousStep() {
    this.currentStep = 1;
  }

  async onSubmit() {
    if (this.registrationForm.valid && this.passwordsMatch) {
      this.loading = true;
      try {
        const { confirmPassword, ...formData } = this.registrationForm.value;
        await this.auth.registerAdmin(formData);
      } catch (error) {
        // Error is already handled in AuthService
      } finally {
        this.loading = false;
      }
    } else {
      Object.keys(this.registrationForm.controls).forEach(field => {
        const control = this.registrationForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  openLoginModal() {
    // Registration here is exclusively the seller/shop sign-up flow, so send
    // the user back to the landing page with the matching Seller Portal
    // login form open — not the generic customer login modal.
    this.router.navigate(['/landing'], { queryParams: { login: 'seller' } });
  }
}
